import { useState } from "react";
import { Hourglass, Check, X, Percent, Ban } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MTabs, Loading, EmptyArt, Chip, ReasonDialog, cardCls, blueBtn } from "@/components/mobile/parts";
import { usePoll } from "@/hooks/useMobileData";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { cn } from "@/lib/utils";

const STATUS = { pending: ["Aguardando", "amber"], approved: ["Liberado", "green"], rejected: ["Recusado", "red"] };

function ApprovalCard({ a, manager, onDecide }) {
  const Icon = a.type === "discount" ? Percent : Ban;
  const [label, tone] = STATUS[a.status];
  return (
    <div className={cn(cardCls, "space-y-2 p-4")} data-testid={`m-approval-${a.id}`}>
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#e3f2fd] text-[#1565c0]"><Icon className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold">Comanda #{a.comanda_number}{a.table_number ? ` · Mesa ${a.table_number}` : ""}{a.customer_name ? ` · ${a.customer_name}` : ""}</p>
          <p className="text-sm text-slate-700" data-testid={`m-approval-desc-${a.id}`}>{a.description}</p>
          {a.reason && <p className="text-xs text-slate-500">Motivo: {a.reason}</p>}
          <p className="text-xs text-slate-400">{a.requested_by} · {fmtDateTime(a.requested_at)}</p>
          {a.status !== "pending" && <p className="text-xs text-slate-500">{label} por {a.decided_by} · {fmtDateTime(a.decided_at)}{a.note ? ` · ${a.note}` : ""}</p>}
        </div>
        <Chip tone={tone}>{a.status === "pending" && <Hourglass className="h-3 w-3" />}{label}</Chip>
      </div>
      {manager && a.status === "pending" && (
        <div className="flex gap-2">
          <Button variant="outline" className="h-11 flex-1 text-red-600" onClick={() => onDecide(a, false)} data-testid={`m-approval-reject-${a.id}`}><X className="mr-1 h-4 w-4" />Recusar</Button>
          <Button className={cn("h-11 flex-1", blueBtn)} onClick={() => onDecide(a, true)} data-testid={`m-approval-approve-${a.id}`}><Check className="mr-1 h-4 w-4" />Liberar</Button>
        </div>
      )}
    </div>
  );
}

export function ApprovalsTab({ manager }) {
  const [status, setStatus] = useState("pending");
  const { data, load } = usePoll(() => api.get(`/approvals?status=${status}`).then((r) => r.data), 10000, [status]);
  const [rejecting, setRejecting] = useState(null);
  const decide = async (a, approve, note = "") => {
    try {
      await api.post(`/approvals/${a.id}/decide`, { approve, note });
      toast.success(approve ? "Liberado" : "Recusado");
      setRejecting(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3">
      <MTabs value={status} onChange={setStatus} items={[["pending", "Aguardando"], ["all", "Todas"]]} testId="m-approvals-filter" />
      {!data ? <Loading /> : !data.length ? (
        <EmptyArt icon={Check} title={status === "pending" ? "Nada aguardando liberação" : "Nenhuma solicitação"} text={manager ? "Pedidos de cancelamento e desconto feitos pelos garçons aparecem aqui." : "Quando você pedir um cancelamento ou desconto, ele aparece aqui."} testId="m-approvals-empty" />
      ) : data.map((a) => <ApprovalCard key={a.id} a={a} manager={manager} onDecide={(x, ok) => (ok ? decide(x, true) : setRejecting(x))} />)}
      <ReasonDialog open={!!rejecting} title="Recusar a solicitação?" label="Observação para o garçom" confirmText="Recusar" onClose={() => setRejecting(null)} onConfirm={(note) => decide(rejecting, false, note)} testId="m-reject" />
    </div>
  );
}
